<template>
    <div class="max-w-4xl mt-4 mb-4 mx-auto p-6 bg-white rounded-lg shadow-md">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-[#035a76]">Termination Processing</h1>
        <button @click="$emit('back')" class="text-gray-500 hover:text-gray-700">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
  
      <!-- Employee Summary -->
      <div class="bg-gray-50 p-4 rounded-md mb-6">
        <h3 class="text-lg font-medium mb-3">Employee Details</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p class="text-sm text-gray-500">Name</p>
            <p class="font-medium">{{ employee.name }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Current Position</p>
            <p class="font-medium">{{ employee.position }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Contract End Date</p>
            <p class="font-medium">{{ formatDate(employee.contractEndDate) }}</p>
          </div>
        </div>
      </div>
  
      <!-- Termination Form -->
      <form @submit.prevent="submitForm" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700">Termination Type*</label>
            <select v-model="form.terminationType" required
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              <option value="">Select type</option>
              <option value="resignation">Resignation</option>
              <option value="retirement">Retirement</option>
              <option value="dismissal">Dismissal</option>
              <option value="end_of_contract">End of Contract</option>
              <option value="mutual_agreement">Mutual Agreement</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Effective Date*</label>
            <input v-model="form.effectiveDate" type="date" required
                   class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
          </div>
        </div>
  
        <div>
          <label class="block text-sm font-medium text-gray-700">Reason for Termination*</label>
          <textarea v-model="form.reason" rows="3" required
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
                    placeholder="Provide detailed reason..."></textarea>
        </div>
  
        <div>
          <label class="block text-sm font-medium text-gray-700">Notice Period Served</label>
          <div class="mt-2 space-y-2">
            <div class="flex items-center">
              <input id="notice-served" v-model="form.noticeServed" type="radio" value="full" 
                     class="h-4 w-4 text-[#035a76] focus:ring-[#035a76] border-gray-300">
              <label for="notice-served" class="ml-2 block text-sm text-gray-700">Full notice period served</label>
            </div>
            <div class="flex items-center">
              <input id="notice-partial" v-model="form.noticeServed" type="radio" value="partial" 
                     class="h-4 w-4 text-[#035a76] focus:ring-[#035a76] border-gray-300">
              <label for="notice-partial" class="ml-2 block text-sm text-gray-700">Partial notice served</label>
            </div>
            <div class="flex items-center">
              <input id="notice-none" v-model="form.noticeServed" type="radio" value="none" 
                     class="h-4 w-4 text-[#035a76] focus:ring-[#035a76] border-gray-300">
              <label for="notice-none" class="ml-2 block text-sm text-gray-700">No notice served</label>
            </div>
          </div>
        </div>
  
        <div v-if="form.noticeServed === 'partial'">
          <label class="block text-sm font-medium text-gray-700">Days Served</label>
          <input v-model.number="form.daysServed" type="number" min="1"
                 class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
        </div>
  
        <div>
          <label class="block text-sm font-medium text-gray-700">Final Settlement Details</label>
          <textarea v-model="form.settlement" rows="3"
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
                    placeholder="Details of final payments, benefits, etc."></textarea>
        </div>
  
        <div>
          <label class="block text-sm font-medium text-gray-700">Exit Checklist</label>
          <div class="mt-2 space-y-2">
            <div v-for="(item, index) in exitChecklist" :key="index" class="flex items-start">
              <div class="flex items-center h-5">
                <input :id="`checklist-${index}`" v-model="item.completed" type="checkbox"
                       class="focus:ring-[#035a76] h-4 w-4 text-[#035a76] border-gray-300 rounded">
              </div>
              <div class="ml-3 text-sm">
                <label :for="`checklist-${index}`" class="font-medium text-gray-700">{{ item.task }}</label>
                <p v-if="item.description" class="text-gray-500">{{ item.description }}</p>
              </div>
            </div>
          </div>
        </div>
  
        <div class="flex justify-end space-x-4">
          <button type="button" @click="$emit('back')"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
            Cancel
          </button>
          <button type="submit"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
            Process Termination
          </button>
        </div>
      </form>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted } from 'vue';
  
  const props = defineProps({
    employeeData: Object,
    contractType: String
  });
  
  const emit = defineEmits(['back', 'submit']);
  
  const employee = props.employeeData || {
    name: 'Kwame Mensah',
    position: 'Mathematics Teacher',
    contractEndDate: '2024-06-30'
  };
  
  const form = ref({
    terminationType: '',
    effectiveDate: '',
    reason: '',
    noticeServed: 'full',
    daysServed: 0,
    settlement: ''
  });
  
  const exitChecklist = ref([
    { task: 'School property returned', description: 'Books, keys, devices, etc.', completed: false },
    { task: 'Final salary processed', completed: false },
    { task: 'Benefits terminated', description: 'Health insurance, etc.', completed: false },
    { task: 'Exit interview conducted', completed: false },
    { task: 'Access revoked', description: 'Email, systems, building access', completed: false }
  ]);
  
  const submitForm = () => {
    emit('submit', {
      type: 'termination',
      employeeId: employee.id,
      ...form.value,
      exitChecklist: exitChecklist.value
    });
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };
  
  onMounted(() => {
    // Set default effective date to today
    form.value.effectiveDate = new Date().toISOString().split('T')[0];
  });
  </script>